import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase.js'

/**
 * Gère les préférences de contenu introductif d'un utilisateur.
 * Persiste dans la colonne `content_prefs` (jsonb) de la table `profiles`.
 *
 * Usage : const { isDismissed, dismiss, markViewed } = useContentPrefs(userId)
 */
export function useContentPrefs(userId) {
  const [prefs, setPrefs] = useState(null)

  useEffect(() => {
    if (!userId) return
    supabase
      .from('profiles')
      .select('content_prefs')
      .eq('id', userId)
      .single()
      .then(({ data }) => setPrefs(data?.content_prefs ?? {}))
  }, [userId])

  const isDismissed = useCallback(
    (key) => prefs?.[key]?.dismissed === true,
    [prefs]
  )

  const dismiss = useCallback(
    async (key) => {
      if (!userId) return
      const updated = {
        ...prefs,
        [key]: { ...prefs?.[key], dismissed: true, dismissed_at: new Date().toISOString() },
      }
      setPrefs(updated)
      await supabase.from('profiles').update({ content_prefs: updated }).eq('id', userId)
    },
    [userId, prefs]
  )

  const markViewed = useCallback(
    async (key) => {
      if (!userId || prefs?.[key]?.first_viewed_at) return
      const updated = {
        ...prefs,
        [key]: { ...prefs?.[key], first_viewed_at: new Date().toISOString() },
      }
      setPrefs(updated)
      await supabase.from('profiles').update({ content_prefs: updated }).eq('id', userId)
    },
    [userId, prefs]
  )

  return { prefs, isDismissed, dismiss, markViewed }
}
